import { Injectable, PLATFORM_ID, Inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, throwError, EMPTY, from } from "rxjs";
import { map, catchError, switchMap } from 'rxjs/operators';

import { CookieService } from 'ngx-cookie';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class APICallerService {
  _FAKED = true;
  backendServerURL!: string;
  options: any;
  isBrowser: boolean;

  constructor(
    private http: HttpClient,
    private cookie: CookieService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.options = {};
    this.isBrowser = isPlatformBrowser(platformId);
  }

  // NOTE: `withToken` is intentionally loose because legacy code passes "" / null
  // and the token behavior is handled by an interceptor.
  getCall<T = any>(url: string, withToken?: any): Observable<T> {
    return this.http
      .get<T>(url)
      .pipe(
        map((response: T) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  getCallNoBearer(url: string): Observable<any> {
    const headers = new HttpHeaders({ setbearer: "no-bearer" });
    return this.http
      .get(url, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  putCall(url: string, data: any, withToken?: any): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json;charset=UTF-8');
    return this.http
      .put(url, data, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  putCallHeader(url: string, data: any): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json;charset=UTF-8');
    return this.http
      .put(url, data, { headers: headers, observe: 'response' })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  deleteCall(url: string, data: any, withToken?: any): Observable<any> {
    return this.http
      .delete(url, data)
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  deleteCallWithData(url: string, data: any, withToken?: any): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: data,
    };
    return this.http
      .delete(url, options)
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  deleteCallWithoutData(url: string, withToken?: any): Observable<any> {
    return this.http
      .delete(url)
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  postCall(url: string, data: any, withToken?: any): Observable<any> {
    return this.http
      .post(url, data)
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  postCallNoBearer(url: string, data: any): Observable<any> {
    const headers = new HttpHeaders({ setbearer: "no-bearer" });
    return this.http
      .post(url, data, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  postCallWithTimeZone(url: string, payload: any): Observable<any> {
    const headers = new HttpHeaders().set('x-User-Timezone', 'America/Los_Angeles');
    return this.http
      .post(url, payload, { headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  getCallWithTimeZone(url: string): Observable<any> {
    const headers = new HttpHeaders().set('x-User-Timezone', 'America/Los_Angeles');
    return this.http
      .get(url, { headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  getCallSeparateBearer(url: string, bearer: string): Observable<any> {
    const headers = new HttpHeaders({
      setbearerSeparate: "separate-bearer",
      setbearerSeparateVal: "Basic " + bearer
    });
    return this.http
      .get(url, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  posCallSeparateBearer(url: string, data: any, bearer: string): Observable<any> {
    const headers = new HttpHeaders({
      setbearerSeparate: "separate-bearer",
      setbearerSeparateVal: "Basic " + bearer
    });
    return this.http
      .post(url, data, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  postDateCall(url: string, data: any, withToken?: any): Observable<any> {
    return this.http
      .post(url, data, { observe: 'response' })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  postWithFormDataandJSONCall(url: string, obj: any, data: any, withToken?: any): Observable<any> {
    const formData: FormData = new FormData();
    if (data != '') {
      formData.append("file", data, data.name);
    }
    const blobOverrides = new Blob([JSON.stringify(obj)], {
      type: 'text/html',
    });
    formData.append('leadNotes', blobOverrides);
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .post(url, formData, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  postWithFormDataCall(url: string, data: any, withToken?: any): Observable<any> {
    const formData: FormData = new FormData();
    formData.append("file", data, data.name);
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .post(url, formData, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  putWithFormDataCall(url: string, data: any, withToken?: any): Observable<any> {
    const formData: FormData = new FormData();
    formData.append("file", data, data.name);
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .put(url, formData, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  postWithFormDataMultiFileCall(url: string, data: any, withToken?: any): Observable<any> {
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .post(url, data, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  // Legacy helper: some apps pass FormData directly under the name `postWithFormData(...)`
  postWithFormData(url: string, formData: FormData, withToken?: any): Observable<any> {
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .post(url, formData, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }
    postWithoutTpHeaderFormDataCall(url: string, data: any, withToken?: any): Observable<any> {
    const headers = new HttpHeaders(); 
      return this.http
        .post(url, data, { headers: headers }).pipe(
          map((response: any) => response),
          catchError((error: any) => throwError(() => error))
        );
    }

  postWithFormDataMultiFile(url: string, formData: FormData, withToken?: any): Observable<any> {
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .post(url, formData, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  putWithFormDataMultiFile(url: string, formData: FormData, withToken?: any): Observable<any> {
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .put(url, formData, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }
  putImagesFormDataMultiFile(url: string, formData: FormData, withToken?: any): Observable<any> {
    const headers = new HttpHeaders();
    return this.http
      .put(url, formData, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  private buildMultipartJsonBody(requestBody: object): { body: string; headers: HttpHeaders } {
    const boundary = '----FormBoundary' + Math.random().toString(36).substring(2);
    const json = JSON.stringify(requestBody);
    const body =
      `--${boundary}\r\n` +
      `Content-Disposition: form-data; name="request"\r\n` +
      `Content-Type: application/json\r\n\r\n` +
      json + `\r\n` +
      `--${boundary}--\r\n`;
    const headers = new HttpHeaders({
      'Content-Type': `multipart/form-data; boundary=${boundary}`,
      tp: 'multipart/form-data'
    });
    return { body, headers };
  }

  postJsonFormData(url: string, requestBody: object, withToken?: any): Observable<any> {
    const { body, headers } = this.buildMultipartJsonBody(requestBody);
    return this.http.post(url, body, { headers }).pipe(
      map((response: any) => response),
      catchError((error: any) => throwError(() => error))
    );
  }

  putJsonFormData(url: string, requestBody: object, withToken?: any): Observable<any> {
    const { body, headers } = this.buildMultipartJsonBody(requestBody);
    return this.http.put(url, body, { headers }).pipe(
      map((response: any) => response),
      catchError((error: any) => throwError(() => error))
    );
  }

  putWithJsonDataAndFile(url: string, data: any): Observable<any> {
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .put(url, data, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  postDataWithFileCall(url: string, formData: FormData): Observable<any> {
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .post(url, formData, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  signupAuthPostData(url: string, data: any): Observable<any> {
    return this.http
      .post(url, data)
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  authPostDataLogin(url: string, formData: any): Observable<any> {
    return this.http
      .post(url, formData.toString())
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  getFile(url: string, contentType: string): Observable<any> {
    if (!this.isBrowser) {
      return EMPTY;
    }
    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      map((response: any) => {
        const blob = new Blob([response.body], { type: contentType });
        const disposition = response.headers.get('Content-Disposition');
        const filename = disposition ? disposition.split('filename=') : [];
        return { res: blob, filename };
      }),
      catchError((error: any) => this.parseFileDownloadError(error))
    );
  }

  private parseFileDownloadError(error: any): Observable<never> {
    const fallbackMessage = 'Unable to download the File now.';
    if (error?.error instanceof Blob) {
      const errorBlob: Blob = error.error;
      return from(errorBlob.text()).pipe(
        switchMap((text: string) => {
          let parsed: any = { message: fallbackMessage };
          try {
            parsed = JSON.parse(text);
            if (!parsed?.message) {
              parsed = { ...parsed, message: fallbackMessage };
            }
          } catch {
            parsed = { message: text || fallbackMessage };
          }
          return throwError(() => ({
            status: error.status,
            error: parsed,
            message: parsed.message
          }));
        })
      );
    }
    return throwError(() => ({
      status: error?.status,
      error: error?.error || { message: fallbackMessage },
      message: error?.error?.message || error?.message || fallbackMessage
    }));
  }

  downloadExcel(url: string, model: any): Observable<any> {
    if (!this.isBrowser) {
      return EMPTY;
    }
    return new Observable(obs => {
      const bearer = this.cookie.get("Bearer");
      const oReq = new XMLHttpRequest();
      oReq.open("POST", url, true);
      oReq.setRequestHeader("content-type", "application/json");
      oReq.setRequestHeader("Authorization", "Bearer " + bearer);
      oReq.responseType = "arraybuffer";

      oReq.onload = function () {
        const arrayBuffer = oReq.response;
        const byteArray = new Uint8Array(arrayBuffer);
        obs.next(byteArray);
      };

      const body = JSON.stringify(model);
      oReq.send(body);
    });
  }

  downloadCSV(url: string, method: string = 'GET', body: any = null): Observable<any> {
    if (!this.isBrowser) {
      return EMPTY;
    }
    return new Observable(obs => {
      const bearer = this.cookie.get("Bearer");
      const oReq = new XMLHttpRequest();
      oReq.open(method, url, true);
      oReq.setRequestHeader("content-type", "application/json");
      oReq.setRequestHeader("Authorization", "Bearer " + bearer);
      oReq.responseType = "arraybuffer";
      oReq.onload = function () {
        const arrayBuffer = oReq.response;
        const byteArray = new Uint8Array(arrayBuffer);
        obs.next(byteArray);
      };
      if (body) {
        oReq.send(JSON.stringify(body));
      } else {
        oReq.send();
      }
    });
  }

  patchWithToken(url: string, data: any, withToken?: any): Observable<any> {
    return this.http
      .patch(url, data)
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  patchCall(url: string, data: any): Observable<any> {
    const headers = new HttpHeaders({ setbearer: "no-bearer" });
    return this.http
      .patch(url, data, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  postCallNoBearerNoData(url: string): Observable<any> {
    const headers = new HttpHeaders({ setbearer: "no-bearer" });
    return this.http
      .post(url, {}, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  downloadFile(url: string, filename: string): Observable<void> {
    if (!this.isBrowser) {
      return EMPTY;
    }
    const headers = new HttpHeaders().set('x-User-Timezone', 'America/Los_Angeles');

    return this.http.get(url, {
      headers,
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((response: any) => {
        // Create a blob from the response
        const blob = new Blob([response.body], { type: response.headers.get('content-type') || 'application/octet-stream' });

        // Create a download link
        const downloadLink = document.createElement('a');
        const downloadUrl = window.URL.createObjectURL(blob);
        downloadLink.href = downloadUrl;
        downloadLink.download = filename;

        // Trigger download
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // Cleanup
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(downloadUrl);
      }),
      catchError((error: any) => throwError(() => error))
    );
  }

  putCallNoBearerNoData(url: string): Observable<any> {
    const headers = new HttpHeaders({ setbearer: "no-bearer" }); // this just ref for interceptor
    return this.http
      .put(url, { headers: headers })
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  putCallNoData(url: string, withToken?: any): Observable<any> {
    return this.http
      .put(url, {})
      .pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }
  postCallHeader(url: string, data: any): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json;charset=UTF-8');
    return this.http
      .post(url, data, { headers: headers, observe: 'response' }).pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }
  postWithFormDataCallMulti(url: string, formData: any, withToken?: any): Observable<any> {
    const headers = new HttpHeaders({ tp: "multipart/form-data" }); // this just ref for interceptor
    return this.http
      .post(url, formData, { headers: headers }).pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }
  postCallHeaderNoToken(url: string, data: any) {
    const headers = new HttpHeaders({ setbearer: "no-bearer" }).set('Content-Type', 'application/json;charset=UTF-8');
    return this.http
      .post(url, data, { headers: headers, observe: 'response' }).pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }
  postCalCall(url: string, data: any, withToken?: any): Observable<any> {
    const headers = new HttpHeaders({ 'Referrer-Policy': 'no-referrer-when-downgrade' });
    return this.http.post(url, data, { headers: headers }).pipe(
      map((response: any) => response),
      catchError((error: any) => throwError(() => error))
    );
  }
  patchCallNoBearer(url: string, data: any): Observable<any> {
    const headers = new HttpHeaders({ setbearer: "no-bearer" }); // this just ref for interceptor
    return this.http
      .patch(url, data, { headers: headers }).pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }
  putCallNoBearer(url: string, data: any): Observable<any> {
    const headers = new HttpHeaders({ setbearer: "no-bearer" }); // this just ref for interceptor
    return this.http
      .put(url, data, { headers: headers }).pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  downloadExcelWithName(url: string, model: any): Observable<any> {
    if (!this.isBrowser) {
      return EMPTY;
    }
    return new Observable(obs => {
      const bearer = this.cookie.get("Bearer");
      const oReq = new XMLHttpRequest();
      oReq.open("POST", url, true);
      oReq.setRequestHeader("content-type", "application/json");
      oReq.setRequestHeader("Authorization", "Bearer " + bearer);
      oReq.responseType = "arraybuffer";

      oReq.onload = function (oEvent) {
        obs.next(oReq);
      };
      const body = JSON.stringify(model);
      oReq.send(body);
    });
  }

  downloadExcelLinkedList(url: string, data: {}): void {
    if (!this.isBrowser) {
      return;
    }
    this.http.post(url, data, { responseType: 'blob' }).subscribe(response => {
      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'LinkedListing_Report.xlsx';
      link.target = '_blank';
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
    });
  }

  postFileWithBlobResponse(url: string, formData: FormData): Observable<any> {
    const bearer = this.cookie.get('Bearer');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${bearer}`,
      'tp': 'multipart/form-data'
    });

    return this.http.post(url, formData, {
      headers: headers,
      observe: 'response',
      responseType: 'blob' as 'json'
    }).pipe(
      map((response: any) => {
        const contentType = response.headers?.get('content-type') || response.body?.type || '';
        return {
          success: true,
          contentType: contentType,
          blob: response.body,
          headers: response.headers
        };
      }),
      catchError((error: any) => {
        if (error.error instanceof Blob) {
          const contentType = error.error.type;
          const blobSize = error.error.size;

          return throwError(() => ({
            success: false,
            contentType: contentType,
            blob: error.error,
            blobSize: blobSize,
            status: error.status
          }));
        } else {
          return throwError(() => ({
            success: false,
            error: error.error,
            message: error.error?.message || error.message || 'Error uploading file',
            status: error.status
          }));
        }
      })
    );
  }

  topdateImageUpload(url: string, obj: any): Observable<any> {
    const amenityId = obj.amenityId;
    const imageId = obj.imageId;
    const fd: FormData = new FormData();
    fd.append('amenityId', amenityId);
    fd.append('imageId', imageId);
    fd.append('file', obj.webAsset);
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .post(url, fd, { headers: headers }).pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  toUploadImageUpload(url: string, obj: any): Observable<any> {
    const amenityId = (obj.amenityId);
    const fd = new FormData();
    fd.append('amenityId', amenityId);
    fd.append('file', obj.webAsset);
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .post(url, fd, { headers: headers }).pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }

  downloadExcelVendor(url: string, model: any): Observable<any> {
    if (!this.isBrowser) {
      return EMPTY;
    }
    return new Observable(obs => {
      const bearer = this.cookie.get("Bearer");
      const oReq = new XMLHttpRequest();
      oReq.open("GET", url, true);
      oReq.setRequestHeader("content-type", "application/json");
      oReq.setRequestHeader("Authorization", "Bearer " + bearer);
      oReq.responseType = "arraybuffer";

      oReq.onload = function (oEvent) {
        const arrayBuffer = oReq.response;
        const byteArray = new Uint8Array(arrayBuffer);

        const responseHeaders = oReq.getAllResponseHeaders();
        const responseData = {
          data: byteArray,
          headers: responseHeaders
        };

        obs.next(responseData);
      };

      const body = JSON.stringify(model);
      oReq.send(body);
    });
  }

  putDataWithFileCall(url: string, data: any, withToken?: any): Observable<any> {
    const headers = new HttpHeaders({ tp: "multipart/form-data" });
    return this.http
      .put(url, data, { headers: headers }).pipe(
        map((response: any) => response),
        catchError((error: any) => throwError(() => error))
      );
  }
}

